import {
  type HTMLAttributes,
  type ReactNode,
  type ElementType,
  type CSSProperties,
} from 'react';
import classNames from 'classnames';
import styles from './styles.module.css';

// Explicit style mappings for CSS modules (dynamic keys don't work with tree-shaking)
const variantStyles: Record<string, string> = {
  h1: styles['typography--h1'],
  h2: styles['typography--h2'],
  h3: styles['typography--h3'],
  h4: styles['typography--h4'],
  h5: styles['typography--h5'],
  h6: styles['typography--h6'],
  body1: styles['typography--body1'],
  body2: styles['typography--body2'],
  caption: styles['typography--caption'],
  caption2: styles['typography--caption2'],
  overline: styles['typography--overline'],
  subtitle1: styles['typography--subtitle1'],
  subtitle2: styles['typography--subtitle2'],
  subtitle3: styles['typography--subtitle3'],
};

const sizeStyles: Record<string, string> = {
  xs: styles['typography--size-xs'],
  sm: styles['typography--size-sm'],
  md: styles['typography--size-md'],
  lg: styles['typography--size-lg'],
  xl: styles['typography--size-xl'],
  '2xl': styles['typography--size-2xl'],
  '3xl': styles['typography--size-3xl'],
  '4xl': styles['typography--size-4xl'],
};

const weightStyles: Record<string, string> = {
  thin: styles['typography--weight-thin'],
  light: styles['typography--weight-light'],
  normal: styles['typography--weight-normal'],
  medium: styles['typography--weight-medium'],
  semibold: styles['typography--weight-semibold'],
  bold: styles['typography--weight-bold'],
  extrabold: styles['typography--weight-extrabold'],
  black: styles['typography--weight-black'],
};

const colorStyles: Record<string, string> = {
  primary: styles['typography--color-primary'],
  secondary: styles['typography--color-secondary'],
  text: styles['typography--color-text'],
  textSecondary: styles['typography--color-textSecondary'],
  error: styles['typography--color-error'],
  success: styles['typography--color-success'],
  warning: styles['typography--color-warning'],
  info: styles['typography--color-info'],
  inherit: styles['typography--color-inherit'],
  white: styles['typography--color-white'],
};

const alignStyles: Record<string, string> = {
  left: styles['typography--align-left'],
  center: styles['typography--align-center'],
  right: styles['typography--align-right'],
  justify: styles['typography--align-justify'],
};

const transformStyles: Record<string, string> = {
  none: styles['typography--transform-none'],
  uppercase: styles['typography--transform-uppercase'],
  lowercase: styles['typography--transform-lowercase'],
  capitalize: styles['typography--transform-capitalize'],
};

export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'body1'
  | 'body2'
  | 'caption'
  | 'caption2'
  | 'overline'
  | 'subtitle1'
  | 'subtitle2'
  | 'subtitle3';

export type TypographySize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

export type TypographyWeight =
  | 'thin'
  | 'light'
  | 'normal'
  | 'medium'
  | 'semibold'
  | 'bold'
  | 'extrabold'
  | 'black';

export type TypographyColor =
  | 'primary'
  | 'secondary'
  | 'text'
  | 'textSecondary'
  | 'error'
  | 'success'
  | 'warning'
  | 'info'
  | 'inherit'
  | 'white';

export type TypographyAlign = 'left' | 'center' | 'right' | 'justify';

export type TypographyTransform = 'none' | 'uppercase' | 'lowercase' | 'capitalize';

export interface TypographyProps extends Omit<HTMLAttributes<HTMLElement>, 'color'> {
  /**
   * The content of the typography component
   */
  children: ReactNode;

  /**
   * Typography variant that determines the semantic element and default styling
   * @default 'body1'
   */
  variant?: TypographyVariant;

  /**
   * Size override (independent of variant)
   */
  size?: TypographySize;

  /**
   * Font weight
   * @default 'normal'
   */
  weight?: TypographyWeight;

  /**
   * Text color variant
   * @default 'text'
   */
  color?: TypographyColor;

  /**
   * Text alignment
   * @default 'left'
   */
  align?: TypographyAlign;

  /**
   * Text transform
   * @default 'none'
   */
  transform?: TypographyTransform;

  /**
   * Whether text should be truncated with ellipsis
   * @default false
   */
  truncate?: boolean;

  /**
   * Number of lines to show before truncating (requires truncate to be true)
   */
  lineClamp?: number;

  /**
   * Whether text should wrap
   * @default true
   */
  noWrap?: boolean;

  /**
   * Controls CSS white-space behavior (overrides noWrap when set)
   */
  whitespace?: CSSProperties['whiteSpace'];

  /**
   * Custom component/element to render as
   * Overrides the default element determined by variant
   */
  component?: ElementType;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Whether to use gutter bottom (adds margin bottom)
   * @default false
   */
  gutterBottom?: boolean;

  /**
   * Whether text should be displayed inline
   * @default false
   */
  inline?: boolean;

  /**
   * Line height as a string (e.g., "1.5", "24px", "1.5em", "150%", "normal")
   */
  lineHeight?: string;
}

/**
 * Typography component for displaying text with consistent styling
 *
 * Supports multiple variants, sizes, weights, colors, and text transformations.
 * Automatically selects appropriate semantic HTML element based on variant.
 */
export const Typography = ({
  children,
  variant = 'body1',
  size,
  weight = 'normal',
  color = 'text',
  align = 'left',
  transform = 'none',
  truncate = false,
  lineClamp,
  noWrap = false,
  whitespace,
  component,
  className,
  gutterBottom = false,
  inline = false,
  lineHeight,
  ...props
}: TypographyProps) => {
  // Determine the HTML element to use
  const getComponent = (): ElementType => {
    if (component) return component;

    switch (variant) {
      case 'h1':
        return 'h1';
      case 'h2':
        return 'h2';
      case 'h3':
        return 'h3';
      case 'h4':
        return 'h4';
      case 'h5':
        return 'h5';
      case 'h6':
        return 'h6';
      case 'body1':
      case 'body2':
      case 'subtitle1':
      case 'subtitle2':
      case 'subtitle3':
        return 'p';
      case 'caption':
      case 'caption2':
      case 'overline':
        return 'span';
      default:
        return 'p';
    }
  };

  const Component = getComponent();

  const typographyClasses = classNames(
    styles.typography,
    variantStyles[variant],
    {
      [sizeStyles[size!]]: size,
      [weightStyles[weight]]: weight,
      [colorStyles[color]]: color,
      [alignStyles[align]]: align,
      [transformStyles[transform]]: transform,
      [styles['typography--truncate']]: truncate && !lineClamp,
      [styles['typography--no-wrap']]: noWrap && !whitespace,
      [styles['typography--gutter-bottom']]: gutterBottom,
      [styles['typography--inline']]: inline,
    },
    className
  );

  const style = {
    ...props.style,
    ...(lineClamp && truncate
      ? {
          WebkitLineClamp: lineClamp,
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical' as const,
          overflow: 'hidden',
        }
      : {}),
    ...(lineHeight !== undefined ? { lineHeight } : {}),
    ...(whitespace !== undefined ? { whiteSpace: whitespace } : {}),
  };

  return (
    <Component
      className={typographyClasses}
      style={style}
      {...props}
    >
      {children}
    </Component>
  );
};

Typography.displayName = 'Typography';

